import 'react-quill/dist/quill.snow.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'features/common/components/Punchup/primitives/forms/pickers/PunchupDateTimePicker/PunchupDateTimePicker.css';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import ReactDatePicker from 'react-datepicker';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupCheckbox } from 'features/common/components/Punchup/primitives/forms/fields/PunchupCheckbox';
import { getData, post } from 'services/api';
import { IdParamsType } from 'utils/types';
import { useIframeDialogClamp } from 'features/common/hooks/useIframeDialogClamp';
import { postToast } from 'features/common/utils/postFlash';
import { LotteryEntriesCounts, LotteryEntryStatus } from 'features/LotteryEntries/types';
import {
	DEFAULT_TEXT_ABOVE_BUTTON,
	DEFAULT_TEXT_BELOW_BUTTON,
} from 'features/LotteryEntries/components/RunLotteryModal/constants';
import { getStatus } from 'features/LotteryEntries/components/RunLotteryModal/utils';
import styles from './PunchupRunLotteryModal.module.css';

export interface PunchupRunLotteryModalProps {
	showModal: boolean;
	closeModal: () => void;
	counts: LotteryEntriesCounts | undefined;
	refetch: () => void;
}

const QUILL_MODULES = {
	toolbar: [
		[{ header: [1, 2, 3, false] }],
		['bold', 'italic', 'underline', 'strike'],
		[{ color: [] }, { background: [] }],
		[{ list: 'ordered' }, { list: 'bullet' }],
		[{ align: [] }],
		['link', 'clean'],
	],
};

const QUILL_FORMATS = [
	'header',
	'bold',
	'italic',
	'underline',
	'strike',
	'color',
	'background',
	'list',
	'bullet',
	'align',
	'link',
];

const STATUS_OPTIONS: LotteryEntryStatus[] = ['pending', 'not_winner', 'winner'];

const PREVIEW_DATE_FORMAT: Intl.DateTimeFormatOptions = {
	weekday: 'long',
	year: 'numeric',
	month: 'long',
	day: 'numeric',
	hour: 'numeric',
	minute: 'numeric',
};

export const PunchupRunLotteryModal: FC<PunchupRunLotteryModalProps> = ({ showModal, closeModal, counts, refetch }) => {
	const { t } = useTranslation();
	const { id: eventId }: IdParamsType = useParams();
	// Clamp to the visible mobile viewport when embedded, so the dialog fits the fold; its fixed footer
	// (Run Lottery / Cancel) then stays on screen while the body scrolls internally (TIC-636).
	// `'fixed'` — this overlay renders inline (not portalled), so it must anchor to the iframe viewport.
	const { isInIframe, overlayStyle, containerStyle } = useIframeDialogClamp(showModal, 'fixed');

	const [typesSelected, setTypesSelected] = useState<LotteryEntryStatus[]>(['pending']);
	const [wheelchairSelected, setWheelchairSelected] = useState(false);
	const [numberOfWinners, setNumberOfWinners] = useState('');
	const [numberOfEntries, setNumberOfEntries] = useState('');
	const [expirationDate, setExpirationDate] = useState<Date>(new Date());
	const [disableLoserEmail, setDisableLoserEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [textAboveButton, setTextAboveButton] = useState(DEFAULT_TEXT_ABOVE_BUTTON);
	const [textBelowButton, setTextBelowButton] = useState(DEFAULT_TEXT_BELOW_BUTTON);

	const dialogRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	const { data: eventData } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getData(`/events/${eventId}`),
		enabled: !!eventId,
	});

	const { data: emailTemplate, isLoading: isLoadingPreview } = useQuery({
		queryKey: ['lotteryEmailPreview', eventId],
		queryFn: () => getData(`/lottery/templates/winner?event_id=${eventId}`),
		enabled: !!eventId,
	});

	const processedEmailPreview = useMemo(() => {
		if (!emailTemplate || !eventData) return emailTemplate;

		const eventDate = new Date(eventData.start_date).toLocaleString('en-US', PREVIEW_DATE_FORMAT);
		const eventImg = eventData.images?.find((img: any) => img.main_image)?.image || eventData.images?.[0]?.image || '';
		const formattedExpirationDate = expirationDate.toLocaleString('en-US', PREVIEW_DATE_FORMAT);

		const processedAboveText = textAboveButton
			.replace(/{{eventName}}/g, eventData.name)
			.replace(/{{eventDate}}/g, eventDate)
			.replace(/{{expirationDate}}/g, formattedExpirationDate);
		const processedBelowText = textBelowButton.replace(/{{expirationDate}}/g, formattedExpirationDate);

		return emailTemplate
			.replace(/{{eventName}}/g, eventData.name)
			.replace(/{{showDate}}/g, eventDate)
			.replace(/{{eventImg}}/g, eventImg)
			.replace(/{{{aboveButtonText}}}/g, processedAboveText)
			.replace(/{{{belowButtonText}}}/g, processedBelowText);
	}, [emailTemplate, eventData, textAboveButton, textBelowButton, expirationDate]);

	const maxWinners = useMemo(() => {
		if (!counts || typesSelected.length === 0) return 0;
		return typesSelected.reduce(
			(acc, type) => acc + counts[(type + (wheelchairSelected ? '_wheelchair' : '')) as keyof LotteryEntriesCounts],
			0
		);
	}, [counts, typesSelected, wheelchairSelected]);

	const maxEntries = useMemo(() => {
		if (!counts || typesSelected.length === 0) return 0;
		return typesSelected.reduce(
			(acc, type) =>
				acc + counts[(type + (wheelchairSelected ? '_wheelchair' : '') + '_entries') as keyof LotteryEntriesCounts],
			0
		);
	}, [counts, typesSelected, wheelchairSelected]);

	const handleRunLottery = async () => {
		if (typesSelected.length === 0) return;
		setIsLoading(true);
		try {
			await post(`/events/${eventId}/lottery/run`, {
				number_of_winners: Number(numberOfWinners),
				number_of_entries: Number(numberOfEntries),
				status: typesSelected,
				wheelchair: wheelchairSelected,
				expiration_date: expirationDate,
				disable_loser_email: disableLoserEmail,
				text_above_button: textAboveButton,
				text_below_button: textBelowButton,
			});
			refetch();
			postToast('success', t('lottery-run-success', 'Lottery run successfully'));
			closeModal();
		} catch {
			postToast('error', t('lottery-run-error', 'Error running lottery'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleChangeNumberOfWinners = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (!value) setNumberOfWinners('');
		else if (value <= maxWinners) setNumberOfWinners(value.toString());
	};

	const handleChangeNumberOfEntries = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (!value) setNumberOfEntries('');
		else if (value <= maxEntries) setNumberOfEntries(value.toString());
	};

	const handleSelectType = (type: LotteryEntryStatus) => {
		setTypesSelected(typesSelected.includes(type) ? typesSelected.filter((t) => t !== type) : [...typesSelected, type]);
	};

	useEffect(() => {
		if (!showModal) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeModal();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [showModal, closeModal]);

	useEffect(() => {
		if (!showModal) return;
		const previouslyFocused = document.activeElement as HTMLElement | null;
		(closeButtonRef.current ?? dialogRef.current)?.focus();
		return () => previouslyFocused?.focus();
	}, [showModal]);

	if (!showModal) return null;

	const overlayClass = [styles.overlay, isInIframe ? styles.iframeOverlay : ''].filter(Boolean).join(' ');

	return (
		<div
			className={overlayClass}
			style={overlayStyle}
			role="dialog"
			aria-modal="true"
			aria-label={t('lottery-run-lottery', 'Run Lottery')}
		>
			<div className={styles.dialog} style={containerStyle} ref={dialogRef} tabIndex={-1}>
				<div className={styles.dialogHeader}>
					<h2 className={styles.dialogTitle}>{t('lottery-run-lottery', 'Run Lottery')}</h2>
					<button
						ref={closeButtonRef}
						className={styles.closeButton}
						type="button"
						onClick={closeModal}
						aria-label={t('button-close', 'Close')}
					>
						<X size={20} />
					</button>
				</div>

				<div className={styles.body}>
					<div className={styles.leftSection}>
						<div className={styles.fieldGroup}>
							<p className={styles.fieldLabel}>{t('lottery-select-status', 'Select status')}</p>
							<div className={styles.checkboxRow}>
								{STATUS_OPTIONS.map((type) => (
									<PunchupCheckbox
										key={type}
										checked={typesSelected.includes(type)}
										onChange={() => handleSelectType(type)}
										label={getStatus(type)}
									/>
								))}
							</div>
						</div>

						<div className={styles.fieldGroup}>
							<p className={styles.fieldLabel}>{t('lottery-run-for', 'Run only for')}</p>
							<PunchupCheckbox
								checked={wheelchairSelected}
								onChange={() => setWheelchairSelected(!wheelchairSelected)}
								label={t('lottery-wheelchair', 'Wheelchair')}
							/>
						</div>

						<div className={styles.fieldGroup}>
							<p className={styles.fieldLabel}>{t('lottery-disable-email', 'Disable email for')}</p>
							<PunchupCheckbox
								checked={disableLoserEmail}
								onChange={() => setDisableLoserEmail(!disableLoserEmail)}
								label={t('lottery-not-winners', 'Not winners')}
							/>
						</div>

						<div className={styles.inputRow}>
							<div className={styles.fieldGroup}>
								<label htmlFor="lottery-winners" className={styles.fieldLabel}>
									{t('lottery-num-tickets', 'Number of tickets')} ({t('lottery-max', 'max')} {maxWinners})
								</label>
								<input
									id="lottery-winners"
									type="number"
									className={styles.numberInput}
									placeholder={t('lottery-enter-tickets', 'Enter number of tickets')}
									value={numberOfWinners}
									onChange={handleChangeNumberOfWinners}
									min={1}
									max={maxWinners}
								/>
							</div>
							<div className={styles.fieldGroup}>
								<label htmlFor="lottery-entries" className={styles.fieldLabel}>
									{t('lottery-num-entries', 'Number of entries')} ({t('lottery-max', 'max')} {maxEntries})
								</label>
								<input
									id="lottery-entries"
									type="number"
									className={styles.numberInput}
									placeholder={t('lottery-enter-winners', 'Enter number of entries')}
									value={numberOfEntries}
									onChange={handleChangeNumberOfEntries}
									min={1}
									max={maxEntries}
								/>
							</div>
						</div>

						<div className={styles.fieldGroup}>
							<label htmlFor="lottery-expiration" className="punchupFormLabel">
								{t('lottery-expiration-date', 'Expiration date')}
							</label>
							<div className="punchupDateTimeInputContainer">
								<ReactDatePicker
									id="lottery-expiration"
									autoComplete="off"
									selected={expirationDate}
									onChange={(date: Date) => setExpirationDate(date)}
									showTimeSelect
									dateFormat="MM/dd/yyyy h:mm aa"
									className="punchup-datepicker"
								/>
								<CalendarDays size={16} className="punchupDateTimeIcon" />
							</div>
						</div>

						<div className={styles.fieldGroup}>
							<p className={styles.fieldLabel}>{t('lottery-text-above', 'Text above claim button')}</p>
							<ReactQuill
								theme="snow"
								value={textAboveButton}
								modules={QUILL_MODULES}
								formats={QUILL_FORMATS}
								onChange={setTextAboveButton}
								placeholder={t('lottery-text-above-placeholder', 'Enter text that will appear above the claim button')}
							/>
						</div>

						<div className={styles.fieldGroup}>
							<p className={styles.fieldLabel}>{t('lottery-text-below', 'Text below claim button')}</p>
							<ReactQuill
								theme="snow"
								value={textBelowButton}
								modules={QUILL_MODULES}
								formats={QUILL_FORMATS}
								onChange={setTextBelowButton}
								placeholder={t('lottery-text-below-placeholder', 'Enter text that will appear below the claim button')}
							/>
						</div>
					</div>

					<div className={styles.rightSection}>
						<p className={styles.previewTitle}>{t('lottery-email-preview', 'Email Preview')}</p>
						<div className={styles.emailPreview}>
							{isLoadingPreview ? (
								<p className={styles.previewPlaceholder}>{t('lottery-preview-loading', 'Loading preview...')}</p>
							) : processedEmailPreview ? (
								<div dangerouslySetInnerHTML={{ __html: processedEmailPreview }} />
							) : (
								<p className={styles.previewPlaceholder}>
									{t('lottery-preview-unavailable', 'Unable to load preview')}
								</p>
							)}
						</div>
					</div>
				</div>

				<div className={styles.footer}>
					<PunchupButton
						variant="secondary"
						type="button"
						onClick={closeModal}
						text={t('button-cancel', 'Cancel')}
						disabled={isLoading}
					/>
					<PunchupButton
						variant="primary"
						type="button"
						onClick={handleRunLottery}
						text={t('button-run-lottery', 'Run Lottery')}
						disabled={isLoading || typesSelected.length === 0 || (!numberOfWinners && !numberOfEntries)}
					/>
				</div>
			</div>
		</div>
	);
};
